import { Separator, Stack, ThemeProvider } from '@fluentui/react';
import { DarkTheme } from '@fluentui/theme-samples';
import { IPrimePharmacyComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import React, { useEffect, useMemo, useState } from 'react';


import CompareNavText from './CompareNavText';
// (Removed duplicate interface and import block)
import { IMarkerSet } from 'views/BlueRelationship/Compare/Compare.constants';
import { BENEFIT_COMPARISON_PANEL_ID, GROUP_NUMBER_COMPARISON_PANEL_ID } from './PharmacyPlan.constants';
import {
    QUICK_NAV_CHILD_STACK_STYLES,
    QUICK_NAV_CHILD_STACK_TOKENS,
    QUICK_NAV_FLOATING_BACKDROP_HIDDEN,
    QUICK_NAV_FLOATING_BACKDROP_VISIBLE,
    QUICK_NAV_FLOATING_PANEL_HIDDEN,
    QUICK_NAV_FLOATING_PANEL_VISIBLE,
    QUICK_NAV_GROUP_STYLE,
    QUICK_NAV_INDENT_STYLE,
    QUICK_NAV_OVERLAY_CONTENT_STYLE,
    QUICK_NAV_SECTION_STACK_STYLES,
    QUICK_NAV_SECTION_STACK_TOKENS,
    QUICK_NAV_STACK_TOKENS,
    QUICK_NAV_STICKY_NESTED_SEGMENT_TITLE_STYLE,
    QUICK_NAV_STICKY_SECTION_TITLE_STYLE,
    QUICK_NAV_STICKY_SEGMENT_TITLE_STYLE,
    QUICK_NAV_STICKY_TAB_TITLE_STYLE,
} from './PharmacyPlan.constants';
import QuickNavSearchControls from './QuickNavSearchControls';

interface IPharmacyQuickNavOverlayProps {
    primePharmacyModel: IPrimePharmacyComparisonVM | null;
    isHidden: boolean;
    markers: IMarkerSet;
    onNavigate: (links: string[]) => void;
    showDifferencesOnly: boolean;
    onDismiss?: () => void;
}

const PharmacyQuickNavOverlay: React.FC<IPharmacyQuickNavOverlayProps> = React.memo((props) => {
    const {
        primePharmacyModel,
        isHidden,
        markers,
        onNavigate,
        showDifferencesOnly,
        onDismiss,
    } = props;

    const [mountOnce, setMountOnce] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [showDifferencesOnlyLocal, setShowDifferencesOnlyLocal] = useState(false);
    const effectiveShowDifferencesOnly = showDifferencesOnly || showDifferencesOnlyLocal;
    const hasBenefitComparison = Boolean(primePharmacyModel?.prime);
    const primeId = primePharmacyModel?.prime?.uniqueId ?? '';
    const hasGroupNumberComparison = (primePharmacyModel?.groupNumbers?.entities?.length ?? 0) > 0;

    // Per-render cache for matchesSearch
    const matchesSearch = useMemo(() => {
        const cache = new Map<string | null | undefined, boolean>();
        return (value?: string | null) => {
            if (!searchQuery) return true;
            if (cache.has(value)) return cache.get(value)!;
            const result = value?.toLowerCase().includes(searchQuery) ?? false;
            cache.set(value, result);
            return result;
        };
    }, [searchQuery]);

    // Per-render caches for recursive search helpers
    const sectionMatchesSearch = useMemo(() => {
        const cache = new WeakMap<object, boolean>();
        return (section: any): boolean => {
            if (!section || typeof section !== 'object') return false;
            if (cache.has(section)) return cache.get(section)!;
            const sectionName = section?.sectionDefinition?.section?.bcbs_name;
            const attributeSets = section?.attributeSets ?? [];
            const result = matchesSearch(sectionName)
                || attributeSets.some((eachMasterAttset: any) =>
                    (eachMasterAttset?.attributeSets ?? []).some((eachAttset: any) =>
                        matchesSearch(eachAttset?.attributeDefinition?.attribute?.bcbs_label)
                    )
                );
            cache.set(section, result);
            return result;
        };
    }, [matchesSearch]);

    const tabMatchesSearch = useMemo(() => {
        const cache = new WeakMap<object, boolean>();
        return (tab: any): boolean => {
            if (!tab || typeof tab !== 'object') return false;
            if (cache.has(tab)) return cache.get(tab)!;
            const tabName = tab?.tabDefinition?.tab?.bcbs_name;
            const sections = tab?.sections ?? [];
            const result = matchesSearch(tabName)
                || sections.some((section: any) => sectionMatchesSearch(section));
            cache.set(tab, result);
            return result;
        };
    }, [matchesSearch, sectionMatchesSearch]);

    const groupNumberSectionMatchesSearch = useMemo(() => {
        const cache = new WeakMap<object, boolean>();
        return (section: any): boolean => {
            if (!section || typeof section !== 'object') return false;
            if (cache.has(section)) return cache.get(section)!;
            const result = matchesSearch(section?.sectionDefinition?.section?.bcbs_name);
            cache.set(section, result);
            return result;
        };
    }, [matchesSearch]);

    const groupNumberTabMatchesSearch = useMemo(() => {
        const cache = new WeakMap<object, boolean>();
        return (tab: any): boolean => {
            if (!tab || typeof tab !== 'object') return false;
            if (cache.has(tab)) return cache.get(tab)!;
            const tabName = tab?.tabDefinition?.tab?.bcbs_name;
            const sections = tab?.sections ?? [];
            const result = matchesSearch(tabName)
                || sections.some((section: any) => groupNumberSectionMatchesSearch(section));
            cache.set(tab, result);
            return result;
        };
    }, [matchesSearch, groupNumberSectionMatchesSearch]);




    // const matchesSearch = (value?: string | null) => {
    //     if (!searchQuery) {
    //         return true;
    //     }
    //     return value?.toLowerCase().includes(searchQuery) ?? false;
    // };

    // const sectionMatchesSearch = (section: any) => {
    //     const sectionName = section?.sectionDefinition?.section?.bcbs_name;
    //     const attributeSets = section?.attributeSets ?? [];
    //     return matchesSearch(sectionName)
    //         || attributeSets.some((eachMasterAttset: any) =>
    //             (eachMasterAttset?.attributeSets ?? []).some((eachAttset: any) =>
    //                 matchesSearch(eachAttset?.attributeDefinition?.attribute?.bcbs_label)
    //             )
    //         );
    // };

    // const tabMatchesSearch = (tab: any) => {
    //     const tabName = tab?.tabDefinition?.tab?.bcbs_name;
    //     const sections = tab?.sections ?? [];
    //     return matchesSearch(tabName)
    //         || sections.some((section: any) => sectionMatchesSearch(section));
    // };

    // const groupNumberSectionMatchesSearch = (section: any) => {
    //     return matchesSearch(section?.sectionDefinition?.section?.bcbs_name);
    // };

    // const groupNumberTabMatchesSearch = (tab: any) => {
    //     const tabName = tab?.tabDefinition?.tab?.bcbs_name;
    //     const sections = tab?.sections ?? [];
    //     return matchesSearch(tabName)
    //         || sections.some((section: any) => groupNumberSectionMatchesSearch(section));
    // };


    useEffect(() => {
        // add keydown event listener to close the panel when press esc, and clean up the event listener when the component unmounts or when onDismiss changes
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss?.();
            }
        };

        if (onDismiss && !isHidden) {
            window.addEventListener('keydown', handleKeyDown);
        };

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onDismiss, isHidden]);

    useEffect(() => {
        // performance optimization: only mount the content when the panel is visible, and add a delay to make sure the animation is smooth
        if (!isHidden && !mountOnce) {
            const timer = window.setTimeout(() => {
                setMountOnce(true);
            }, 100);

            return () => window.clearTimeout(timer);
        }

        return undefined;
    }, [isHidden, mountOnce]);


    return (
        <ThemeProvider theme={DarkTheme}>
            {/* Backdrop overlay */}
            <div
                style={isHidden ? QUICK_NAV_FLOATING_BACKDROP_HIDDEN : QUICK_NAV_FLOATING_BACKDROP_VISIBLE}
                onClick={props.onDismiss}
            />

            {/* Floating panel from left */}
            <div style={isHidden ? QUICK_NAV_FLOATING_PANEL_HIDDEN : QUICK_NAV_FLOATING_PANEL_VISIBLE}>
                <div style={QUICK_NAV_OVERLAY_CONTENT_STYLE}>
                    {mountOnce && (
                        <Stack tokens={QUICK_NAV_STACK_TOKENS}>
                            <QuickNavSearchControls
                                isHidden={isHidden}
                                mountOnce={mountOnce}
                                showDifferencesOnly={effectiveShowDifferencesOnly}
                                onSearch={setSearchQuery}
                                onShowDifferencesOnlyChange={(checked) => setShowDifferencesOnlyLocal(checked)}
                            />
                            {hasBenefitComparison && (
                                <>
                                    <Separator />
                                    <CompareNavText
                                        markers={markers}
                                        changed={primePharmacyModel?.prime?.changed}
                                        label={`Benefit Comparison`}
                                        style={QUICK_NAV_STICKY_SEGMENT_TITLE_STYLE}
                                        onClick={() => onNavigate([BENEFIT_COMPARISON_PANEL_ID, primeId])}
                                    />
                                    <CompareNavText
                                        markers={markers}
                                        changed={primePharmacyModel?.prime?.propertiesVM.changed}
                                        label={`Properties`}
                                        style={QUICK_NAV_INDENT_STYLE}
                                        onClick={() => onNavigate([BENEFIT_COMPARISON_PANEL_ID, primeId, `${primeId}-properties`])}
                                    />
                                    {primePharmacyModel?.prime?.tabsVM.map((tab) => {

                                        if (effectiveShowDifferencesOnly && !tab.changed) {
                                            return null;
                                        }

                                        if (!tabMatchesSearch(tab)) {
                                            return null;
                                        }

                                        const tabKey = tab.tabDefinition.tab.bcbs_config_tabid!;
                                        return (
                                            <Stack key={tabKey} style={QUICK_NAV_INDENT_STYLE}>
                                                <CompareNavText
                                                    markers={markers}
                                                    changed={tab.changed}
                                                    label={tab.tabDefinition.tab.bcbs_name}
                                                    style={QUICK_NAV_STICKY_TAB_TITLE_STYLE}
                                                    onClick={() => onNavigate([BENEFIT_COMPARISON_PANEL_ID, primeId, `${primeId}-${tabKey}`])}
                                                />
                                                <Stack tokens={QUICK_NAV_CHILD_STACK_TOKENS} styles={QUICK_NAV_CHILD_STACK_STYLES}>
                                                    {tab.sectionsVM.map((section) => {

                                                        if (effectiveShowDifferencesOnly && !section.changed) {
                                                            return null;
                                                        }

                                                        if (!sectionMatchesSearch(section)) {
                                                            return null;
                                                        }

                                                        const sectionKey = section.sectionDefinition.section.bcbs_config_sectionid!;
                                                        return (
                                                            <Stack key={`${primeId}-${sectionKey}`}>
                                                                <CompareNavText
                                                                    markers={markers}
                                                                    changed={section.changed}
                                                                    label={section.sectionDefinition.section.bcbs_name}
                                                                    variant='smallPlus'
                                                                    style={QUICK_NAV_STICKY_SECTION_TITLE_STYLE}
                                                                    onClick={() => onNavigate([
                                                                        BENEFIT_COMPARISON_PANEL_ID,
                                                                        primeId,
                                                                        `${primeId}-${tabKey}`,
                                                                        `${primeId}-${sectionKey}`,
                                                                    ])}
                                                                />
                                                                <Stack tokens={QUICK_NAV_SECTION_STACK_TOKENS} styles={QUICK_NAV_SECTION_STACK_STYLES}>
                                                                    {section.attributeSetsVM.map((eachMasterAttset) => {
                                                                        return eachMasterAttset.attributeSets.map((eachAttset, index) => {

                                                                            if (effectiveShowDifferencesOnly && !eachAttset.changed) {
                                                                                return null;
                                                                            }

                                                                            if (!matchesSearch(eachAttset.definition.bcbs_config_attribute.bcbs_label)) {
                                                                                return null;
                                                                            }

                                                                            const masterAttributeId = eachAttset.definition.bcbs_config_masterattribute.bcbs_config_masterattributeid!;
                                                                            return (
                                                                                <Stack key={`${masterAttributeId}-${index}`}>
                                                                                    <CompareNavText
                                                                                        markers={markers}
                                                                                        changed={eachAttset.changed}
                                                                                        label={eachAttset.definition.bcbs_config_attribute.bcbs_label}
                                                                                        variant='small'
                                                                                        onClick={() => onNavigate([
                                                                                            BENEFIT_COMPARISON_PANEL_ID,
                                                                                            primeId,
                                                                                            `${primeId}-${tabKey}`,
                                                                                            `${primeId}-${sectionKey}`,
                                                                                            `${primeId}-${masterAttributeId}-${index}`,
                                                                                        ])}
                                                                                    />
                                                                                </Stack>
                                                                            );
                                                                        });
                                                                    })}
                                                                </Stack>
                                                            </Stack>
                                                        );
                                                    })}
                                                </Stack>
                                            </Stack>
                                        );
                                    })}
                                </>
                            )}
                            {hasGroupNumberComparison && (
                                <>
                                    <Separator />
                                    <CompareNavText
                                        markers={markers}
                                        changed={primePharmacyModel?.groupNumbers?.changed}
                                        label={`Group Numbers Comparison`}
                                        style={QUICK_NAV_STICKY_SEGMENT_TITLE_STYLE}
                                        onClick={() => onNavigate([GROUP_NUMBER_COMPARISON_PANEL_ID])}
                                    />
                                    {primePharmacyModel?.groupNumbers?.entities.map((eachGroupNumberVM, gnIndex) => {

                                        if (!groupNumberTabMatchesSearch(eachGroupNumberVM)) {
                                            return null;
                                        }

                                        const groupNumberId = eachGroupNumberVM.uniqueId;

                                        const tabsElements = eachGroupNumberVM.tabsVM.map((tab) => {

                                            if (effectiveShowDifferencesOnly && !tab.changed) {
                                                return null;
                                            }

                                            if (!tabMatchesSearch(tab)) {
                                                return null;
                                            }

                                            const tabKey = `${groupNumberId}-${tab.tabDefinition.tab.bcbs_config_tabid}`;
                                            return (
                                                <Stack tokens={QUICK_NAV_SECTION_STACK_TOKENS} style={QUICK_NAV_INDENT_STYLE} key={tabKey}>
                                                    <CompareNavText
                                                        markers={markers}
                                                        changed={tab.changed}
                                                        label={tab.tabDefinition.tab.bcbs_name}
                                                        style={QUICK_NAV_STICKY_TAB_TITLE_STYLE}
                                                        onClick={() => onNavigate([
                                                            GROUP_NUMBER_COMPARISON_PANEL_ID,
                                                            groupNumberId,
                                                            tabKey,
                                                        ])}
                                                    />
                                                    <Stack tokens={QUICK_NAV_CHILD_STACK_TOKENS} styles={QUICK_NAV_CHILD_STACK_STYLES}>
                                                        {tab.sectionsVM.map((section) => {

                                                            if (effectiveShowDifferencesOnly && !section.changed) {
                                                                return null;
                                                            }

                                                            if (!groupNumberSectionMatchesSearch(section)) {
                                                                return null;
                                                            }

                                                            const sectionKey = `${groupNumberId}-${section.sectionDefinition.section.bcbs_config_sectionid}`;
                                                            return (
                                                                <CompareNavText
                                                                    markers={markers}
                                                                    key={sectionKey}
                                                                    changed={section.changed}
                                                                    label={section.sectionDefinition.section.bcbs_name}
                                                                    variant='small'
                                                                    style={QUICK_NAV_STICKY_SECTION_TITLE_STYLE}
                                                                    onClick={() => onNavigate([
                                                                        GROUP_NUMBER_COMPARISON_PANEL_ID,
                                                                        groupNumberId,
                                                                        tabKey,
                                                                        sectionKey,
                                                                    ])}
                                                                />
                                                            );
                                                        })}
                                                    </Stack>
                                                </Stack>
                                            );
                                        });

                                        return (
                                            <Stack tokens={QUICK_NAV_STACK_TOKENS} style={QUICK_NAV_GROUP_STYLE} key={groupNumberId}>
                                                <CompareNavText
                                                    markers={markers}
                                                    changed={eachGroupNumberVM.changed}
                                                    label={eachGroupNumberVM.friendlyName}
                                                    style={QUICK_NAV_STICKY_NESTED_SEGMENT_TITLE_STYLE}
                                                    onClick={() => onNavigate([
                                                        GROUP_NUMBER_COMPARISON_PANEL_ID,
                                                        groupNumberId,
                                                    ])}
                                                />
                                                <CompareNavText
                                                    markers={markers}
                                                    changed={eachGroupNumberVM.propertiesVM.changed}
                                                    label={`Properties`}
                                                    style={QUICK_NAV_INDENT_STYLE}
                                                    onClick={() => onNavigate([
                                                        GROUP_NUMBER_COMPARISON_PANEL_ID,
                                                        groupNumberId,
                                                        `${groupNumberId}-properties`,
                                                    ])}
                                                />
                                                {tabsElements}
                                            </Stack>
                                        );
                                    })}
                                </>
                            )}
                        </Stack>
                    )}
                </div>
            </div>
        </ThemeProvider>
    );
});

export default PharmacyQuickNavOverlay;